import type { ContactListFilters } from "@/lib/queries";

/**
 * Stepping from one contact to the next, as GoHighLevel's "1/12142 ‹ ›" does: the record knows the
 * list it was opened from and walks that list in its order. Pure, for `test/contact-pager.test.ts`.
 */

/** Where a contact was opened from: the list's filters as the API takes them, the list page's own
 *  query string (Contacts goes back to the same list), and the contact's place in the list, from 0.
 *  It travels in the router's history state, so Back and a reload keep it and a shared link (which
 *  has none) shows no pager. */
export interface ListPlace {
  filters: ContactListFilters;
  search: string;
  index: number;
}

/** A place read back from history state, or null for anything else: a state written by another page,
 *  or by an older console. */
export function placeFromState(state: unknown): ListPlace | null {
  if (!state || typeof state !== "object") return null;
  const place = (state as { list?: unknown }).list;
  if (!place || typeof place !== "object") return null;
  const { filters, search, index } = place as Partial<ListPlace>;
  if (!filters || typeof filters !== "object" || Array.isArray(filters)) return null;
  if (typeof search !== "string" || (search !== "" && !search.startsWith("?"))) return null;
  if (typeof index !== "number" || !Number.isInteger(index) || index < 0) return null;
  return { filters, search, index };
}

/** The history state that carries a place. */
export function placeState(place: ListPlace): { list: ListPlace } {
  return { list: place };
}

/** Rows read around a place: two before it and two after, so the contact is found even when a lead
 *  came in at the top of the list (or left it) since it was opened. */
export const WINDOW = 5;
const BEFORE = 2;

/** Where the window around a place starts. */
export function windowOffset(index: number): number {
  return Math.max(0, index - BEFORE);
}

/** The contact's place in a window read at `offset`, with the contacts on either side; null when it
 *  is no longer in reach (the list moved too far), which shows no arrows rather than wrong ones. */
export function neighbours(
  ids: readonly string[],
  offset: number,
  id: string,
): { index: number; prev: string | null; next: string | null } | null {
  const at = ids.indexOf(id);
  if (at < 0) return null;
  return { index: offset + at, prev: ids[at - 1] ?? null, next: ids[at + 1] ?? null };
}

/** "3 of 12,142", or "3" while the total is unknown. */
export function placeText(index: number, total: number | null): string {
  const n = (index + 1).toLocaleString("en-US");
  return total === null ? n : `${n} of ${total.toLocaleString("en-US")}`;
}
